import { useEffect, useRef, useState } from 'react'
import {
  bareField,
  fieldNamed,
  timelineDql,
  timelineSplitDql,
  useSearchJob,
  type SearchWindow,
  type TimesliceForm,
  type UseSearchJob,
} from '../../search'
import {
  buildBuckets,
  buildSplitBuckets,
  loadTimesliceForm,
  pickTimeslice,
  querySpanMs,
  saveTimesliceForm,
  resolveTimelineColumns,
  timesliceFormKey,
  type SplitBuckets,
  type TimeBucket,
} from '../../timeline'

export interface TimelineState {
  /** Bars or area — a remembered working style. */
  style: 'bars' | 'area'
  setStyle: (v: 'bars' | 'area') => void
  /** The validated split field for the CURRENT result, or null. */
  split: string | null
  /** The raw selection — persists across searches, validated per result. */
  setSplitSel: (field: string | null) => void
  /** True when this scope's engine refused the two-column groupby grammar. */
  splitUnsupported: boolean
  /** True while the shown timeline belongs to the query on screen. */
  current: boolean
  isSplit: boolean
  phase: UseSearchJob['phase']
  slice: { label: string; ms: number } | null
  /** The time field, spelled as this result spells it. */
  timeField: string
  buckets: TimeBucket[]
  stack: (SplitBuckets & { series: string[] }) | null
}

/**
 * The histogram's whole lifecycle, extracted from Datafabric · Search so the
 * hunt cockpit reuses it: a third search job that runs AUTOMATICALLY after
 * each DQL search with a knowable span — unlike facets, a timeline's whole
 * value is being there before anyone asks.
 *
 * The timeslice bucket FIELD NAME is unverified against the live engine, so
 * the first ever run in a scope probes: 'timeslice', then 'CNAMTime';
 * rejections are synchronous, and the accepted form is remembered in
 * localStorage ('none' if both refuse — the timeline stays hidden and the
 * probe never repeats). The split grammar gets the same treatment per
 * session.
 *
 * @param job       The MAIN search job whose results the timeline shadows.
 * @param window    The absolute window the main query ran under, if any.
 * @param displayValue  Formats split series names (stringified epochs → timestamps).
 */
export function useTimelineJobs({
  clusterId,
  scopeId,
  job,
  window: urlWin,
  displayValue,
}: {
  clusterId: string
  scopeId: string
  job: UseSearchJob
  window?: SearchWindow
  displayValue: (field: string, value: unknown) => string
}): TimelineState {
  // One page holds every bucket: timelineDql caps at 2000 rows and
  // pickTimeslice at ~90 buckets, so the default 50-row page would silently
  // drop bars past the 50th.
  const timelineJob = useSearchJob({ clusterId, scopeId, initialPageSize: 2000 })
  const tsKey = timesliceFormKey(clusterId, scopeId)
  /** Session marker for a scope whose engine refused the two-column groupby. */
  const tsSplitKey = `${tsKey}.split`
  const tlAttempt = useRef<{ form: TimesliceForm; query: string; slice: string; win?: SearchWindow; timeField: string; split: string | null } | null>(null)
  const tlRanFor = useRef<string | null>(null)

  // Bars or area — a working style, remembered like the raw-view choice.
  const [tlStyle, setTlStyle] = useState<'bars' | 'area'>(() => {
    try {
      return globalThis.localStorage.getItem('bloo.search.tlstyle') === 'area' ? 'area' : 'bars'
    } catch {
      return 'bars'
    }
  })
  const chooseTlStyle = (v: 'bars' | 'area') => {
    setTlStyle(v)
    try {
      globalThis.localStorage.setItem('bloo.search.tlstyle', v)
    } catch {
      // Preference only.
    }
  }

  // The split selection PERSISTS across searches — an analyst who splits by
  // Action wants the next search split too. Validity is derived instead:
  // the field must exist in the current result, else the split lies dormant
  // until a result that carries it.
  const [tlSplitSel, setTlSplitSel] = useState<string | null>(null)
  const splitUnsupported = loadTimesliceForm(tsSplitKey) === 'none'
  const tlSplit =
    !splitUnsupported &&
    tlSplitSel &&
    job.columns.some((c) => bareField(c).toLowerCase() === tlSplitSel.toLowerCase())
      ? tlSplitSel
      : null

  useEffect(() => {
    // Gated on job.page, not taskId alone: the fallback probe names the time
    // field AS THIS RESULT SPELLS IT (opaque, per-stream cased), and the
    // spelling only arrives with the first result page.
    if (!job.taskId || !job.ranQuery || job.ranDialect !== 'dql' || !job.page) return
    // Keyed on task AND split, so choosing a split re-runs for the same task.
    const runKey = `${job.taskId}|${tlSplit ?? ''}`
    if (tlRanFor.current === runKey) return
    tlRanFor.current = runKey
    if (loadTimesliceForm(tsKey) === 'none') return
    const span = querySpanMs(job.ranQuery, urlWin)
    if (!span) return
    const slice = pickTimeslice(span)
    const form: TimesliceForm = loadTimesliceForm(tsKey) === 'cnamtime' ? 'cnamtime' : 'timeslice'
    const timeField = fieldNamed(job.page.fields, 'CNAMTime') ?? 'CNAMTime'
    const tq = tlSplit
      ? timelineSplitDql(job.ranQuery, slice.label, form, timeField, tlSplit)
      : timelineDql(job.ranQuery, slice.label, form, timeField)
    if (!tq) return
    tlAttempt.current = { form, query: job.ranQuery, slice: slice.label, win: urlWin, timeField, split: tlSplit }
    timelineJob.run(tq, urlWin)
    // urlWin/timelineJob change identity every render; tlRanFor gates real work.
  }, [job.taskId, job.ranQuery, job.ranDialect, job.page, urlWin, timelineJob, tsKey, tlSplit])

  // The probe's second leg, and its conclusion — for both the timeslice form
  // and the split grammar.
  useEffect(() => {
    const att = tlAttempt.current
    if (!att || !timelineJob.queryError) return
    if (loadTimesliceForm(tsKey) === null) {
      // Still probing the timeslice form itself.
      if (att.form === 'timeslice') {
        const tq = att.split
          ? timelineSplitDql(att.query, att.slice, 'cnamtime', att.timeField, att.split)
          : timelineDql(att.query, att.slice, 'cnamtime', att.timeField)
        if (tq) {
          tlAttempt.current = { ...att, form: 'cnamtime' }
          timelineJob.run(tq, att.win)
          return
        }
      }
      saveTimesliceForm(tsKey, 'none')
      tlAttempt.current = null
      return
    }
    if (att.split) {
      // The plain form is known-good, so this rejection is the SPLIT grammar.
      // Remember for the session and fall back to the plain timeline.
      saveTimesliceForm(tsSplitKey, 'none')
      const form = loadTimesliceForm(tsKey) === 'cnamtime' ? 'cnamtime' : 'timeslice'
      const tq = timelineDql(att.query, att.slice, form, att.timeField)
      if (tq) {
        tlAttempt.current = { ...att, form, split: null }
        timelineJob.run(tq, att.win)
        return
      }
    }
    // A known-good form failed on THIS query — query-specific, not grammar.
    tlAttempt.current = null
  }, [timelineJob.queryError, timelineJob, tsKey, tsSplitKey])

  useEffect(() => {
    const att = tlAttempt.current
    if (!att || timelineJob.phase !== 'done') return
    if (loadTimesliceForm(tsKey) === null) saveTimesliceForm(tsKey, att.form)
  }, [timelineJob.phase, tsKey])

  /**
   * Timeline visibility is DERIVED, not stored: the shown buckets must be
   * the ones counted for the query on screen, so the check recomputes what
   * this search's timeline query would be and compares it to what actually
   * ran — a stale histogram over the previous search never renders.
   */
  const tlSlice = job.ranQuery
    ? (() => {
        const span = querySpanMs(job.ranQuery, urlWin)
        return span ? pickTimeslice(span) : null
      })()
    : null
  const tlTimeField = job.page ? (fieldNamed(job.page.fields, 'CNAMTime') ?? 'CNAMTime') : 'CNAMTime'
  const tlSplitQs: string[] =
    job.ranQuery && tlSlice && tlSplit
      ? [
          timelineSplitDql(job.ranQuery, tlSlice.label, 'timeslice', tlTimeField, tlSplit),
          timelineSplitDql(job.ranQuery, tlSlice.label, 'cnamtime', tlTimeField, tlSplit),
        ].filter((s): s is string => s !== null)
      : []
  const tlPlainQs: string[] =
    job.ranQuery && tlSlice
      ? [
          // The plain forms stay candidates even with a split chosen — the
          // split-rejected fallback runs them for the same search.
          timelineDql(job.ranQuery, tlSlice.label, 'timeslice'),
          timelineDql(job.ranQuery, tlSlice.label, 'cnamtime', tlTimeField),
        ].filter((s): s is string => s !== null)
      : []
  // Split-ness comes from WHICH query ran, never from the result's column
  // count — an engine free to add columns must not turn a plain timeline
  // into a phantom split.
  const tlIsSplit = !!timelineJob.ranQuery && tlSplitQs.includes(timelineJob.ranQuery)
  const tlCurrent =
    tlIsSplit || (!!timelineJob.ranQuery && tlPlainQs.includes(timelineJob.ranQuery))
  const tlFields = timelineJob.page?.fields ?? []

  // Column roles by name and value shape, never position — see
  // resolveTimelineColumns for the observed live failure this prevents.
  const {
    count: tlCountCol,
    bucket: tlBucketCol,
    split: tlSplitCol,
  } = resolveTimelineColumns(
    tlFields,
    timelineJob.page?.rows ?? [],
    tlTimeField,
    tlIsSplit ? tlSplit : null,
  )

  const tlBuckets =
    tlCurrent && !tlIsSplit && timelineJob.phase === 'done' && timelineJob.page && tlBucketCol && tlCountCol && tlSlice
      ? buildBuckets(timelineJob.page.rows, tlBucketCol, tlCountCol, tlSlice.ms)
      : []
  const tlStackRaw =
    tlIsSplit && timelineJob.phase === 'done' && timelineJob.page && tlBucketCol && tlSplitCol && tlCountCol && tlSlice
      ? buildSplitBuckets(timelineJob.page.rows, tlBucketCol, tlSplitCol, tlCountCol, tlSlice.ms)
      : null
  // Series names are Map keys and therefore strings — displayValue re-reads
  // stringified epochs so a temporal split labels as timestamps, not digits.
  const tlStack = tlStackRaw
    ? {
        ...tlStackRaw,
        series: tlStackRaw.series.map((s, i) =>
          i === tlStackRaw.series.length - 1 && s === 'Other' ? s : displayValue(tlSplitCol ?? '', s),
        ),
      }
    : null

  return {
    style: tlStyle,
    setStyle: chooseTlStyle,
    split: tlSplit,
    setSplitSel: setTlSplitSel,
    splitUnsupported,
    current: tlCurrent,
    isSplit: tlIsSplit,
    phase: timelineJob.phase,
    slice: tlSlice,
    timeField: tlTimeField,
    buckets: tlBuckets,
    stack: tlStack,
  }
}
